import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import { ArrowBack, Delete, Edit } from '@mui/icons-material';

import { AdminRoles } from '../config/DiactiveTypes';
import { deleteHospital, getHospitalById } from '../services/HospitalService';
import { getUserFullName } from '../services/UserService';
import { formatDateTime } from '../utils/DiactiveUtils';
import DetailRow from '../components/common/DetailRow';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';

const HospitalDetails = ({ role }) => {
  const { hospitalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [createdByName, setCreatedByName] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const canEditOrDelete = AdminRoles.includes(role);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const data = await getHospitalById(hospitalId);
        if (!data) throw new Error(`No se ha encontrado el hospital ${hospitalId}`);
        setHospital(data);
        if (data.createdBy) {
          const name = await getUserFullName(data.createdBy);
          setCreatedByName(name);
        } else {
          setCreatedByName(null);
        }
      } catch (error) {
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hospitalId]);

  const handleBack = () => {
    if (location.state?.fromFilteredTable) {
      navigate(-1);
    } else {
      navigate('/hospitals/');
    }
  };

  const handleEdit = () => navigate(`/hospital/edit/${hospitalId}`);

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteHospital(hospitalId);
      setOpenDeleteDialog(false);
      navigate('/hospitals/', {
        state: { successMessage: 'El hospital se ha eliminado correctamente' },
      });
    } catch (error) {
      setOpenDeleteDialog(false);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hospital) {
    return (
      <Container sx={{ pt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 3 }}>
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ mt: 10 }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onCancel={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar hospital"
        description="¿Seguro que quieres eliminar este hospital de forma permanente? Esta acción no se puede deshacer."
        loading={deleting}
      />
      <Paper elevation={3} sx={{ padding: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} aria-label="back">
            <ArrowBack color="primary" sx={{ fontSize: 40 }} />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ ml: 1, flex: 1 }}>
            {hospital.name}
          </Typography>
          {canEditOrDelete && (
            <>
              <IconButton onClick={handleEdit} aria-label="edit">
                <Edit color="primary" />
              </IconButton>
              <IconButton onClick={() => setOpenDeleteDialog(true)} aria-label="delete">
                <Delete color="error" />
              </IconButton>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 0, md: 6 },
          }}
        >
          <Box>
            <DetailRow label="Nombre" value={hospital.name || '—'} />
            <DetailRow label="ID" value={hospital.id} />
          </Box>
          <Box>
            <DetailRow label="Nombre corto" value={hospital.shortName || '—'} />
            <DetailRow label="Fecha de alta" value={formatDateTime(hospital.createdAt)} />
            <DetailRow label="Creado por" value={createdByName ?? '—'} />
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default HospitalDetails;
